import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import CustomBreadcrumb, { CustomBreadcrumbItem } from '../../components/Breadcrumb';
import useHtmlTitle from '../../components/Utilities/useHtmlTitle';
import useBodyClass from '../../components/Utilities/useBodyClass';
import BlockUISpinner from '../../components/BlockUISpinner';
import HeaderIntroText from '../../components/HeaderIntroText';
import InTextLink from '../../components/InTextLink';

const FAQs = () => {
    const [isLoading, setIsLoading] = useState(false);

    const breadcrumbs: CustomBreadcrumbItem[] = [
        { to: '/', text: 'Home' },
        { to: '/help-guide', text: 'Help guide' },
        { to: '', text: 'Frequently Asked Questions (FAQs)' },
    ];
    useHtmlTitle('Frequently Asked Questions (FAQs) - Help guide | NMI Services portal');
    useBodyClass('help-guide');

    return (
        <>
            {isLoading && (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )}
            <div aria-busy={isLoading} aria-live='polite' aria-atomic='true'>
                <Container fluid className='default-banner-background mb-5'>
                    <Container>
                        <Row>
                            <Col>
                                <CustomBreadcrumb breadcrumbs={breadcrumbs} />
                            </Col>
                        </Row>
                        <Row className='gs-wrapper'>
                            <Col md={12} lg={9}>
                                <h1 id='page-title' tabIndex={-1} className='banner-title mb-2'>
                                    Frequently Asked Questions (FAQs)
                                </h1>
                                <HeaderIntroText>
                                    <strong>
                                        Help guide
                                    </strong>
                                </HeaderIntroText>
                            </Col>
                        </Row>
                    </Container>
                </Container>
                <Container>
                    <Row className='mb-4'>
                        {/* <Col md={4} lg={3}>
                            <nav id='navigate-page' className='mb-4' aria-label='On this page'>
                                <h2 className='h4'>On this page</h2>
                                <div className='menu-wrapper'>
                                    <ol>
                                        <li>
                                            <HashLink to='#faqs-01'>
                                                Q1
                                            </HashLink>
                                        </li>
                                        <li>
                                            <HashLink to='#faq-02'>
                                                Q2
                                            </HashLink>
                                        </li>
                                        <li>
                                            <HashLink to='#faqss-03'>
                                                Q3
                                            </HashLink>
                                        </li>
                                        <li>
                                            <HashLink to='#faqs-contactus'>
                                                Need further assistance?
                                            </HashLink>
                                        </li>
                                    </ol>
                                </div>
                            </nav>
                        </Col>
                        <Col md={8} lg={9}>  */}
                        <Col>
                            <h2 className='visually-hidden'>
                                List of Frequently Asked Questions (FAQs) with answers
                            </h2>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-01' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>What NMI services are available to clients via the NMI Services Portal?</h3>
                                            <p>
                                                The NMI Services Portal enables clients to access NMI&apos;s physical Test and Calibration services.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-02' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>Why do I need to use myID and the Relationship Authorisation Manager (RAM) tool to access the Portal?</h3>
                                            <p>
                                                The use of myID and the RAM tool ensures all clients data is secure and enables the NMI to deliver secure, efficient, and sustainable services.
                                            </p>
                                            <p>Identity validation is necessary for the data security of each client. A calibration service request, quote acceptance and recipient of report is a commercial transaction between the NMI and a client with regulatory and compliance significance.</p>
                                            <p>The identity validation, even for existing NMI clients, is required to ensure the requestor of the service and recipient of the service is:</p>
                                            <ol type='a'>
                                                <li>who they purport to be, and</li>
                                                <li>are authorised to request a service and receive a calibration report as a representative of an authorised and validated organisation/client of the NMI.</li>
                                            </ol>
                                            <p>
                                                Over 65 government online services use the same identity validation and authorisation model (myID and RAM) as the NMI Services Portal.
                                            </p>
                                            <p>
                                                {'For more information on how to set up your access to online services, including the NMI Service Portal using myID and RAM, visit the '}
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://www.ato.gov.au/online-services/businesses-and-organisations-online-services/set-up-your-access-to-online-services-for-business'
                                                >
                                                    Australian Taxation Office website
                                                </InTextLink>
                                                .
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-03' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>How do I set up my myID?</h3>
                                            <p>
                                                The myID app is a new way to access a range of government online services for personal and business matters, instead of usernames and passwords.
                                                <br />
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://www.myid.gov.au/how-set-myid'
                                                >
                                                    Learn how to set up your myID
                                                </InTextLink>
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-04' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>What is the difference between myID and myGov?</h3>
                                            <p>
                                                myID is different to myGov. myID is an app that you download to your smart device. It&apos;s a secure digital identity used to prove who you are when you log in to online government services to conduct activities related to business administration. This includes using the NMI Services Portal to submit a Test and Calibration service request. You must use myID together with the Relationship Authorisation Manager tool (RAM) to access the NMI Services Portal on behalf of your organisation.
                                            </p>
                                            <p>
                                                myGov is a personal account that lets you link online government services for your own personal use (e.g. Centrelink, ATO, Medicare). myGov cannot be used to access government services on behalf of your organisation.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-05' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>Do all staff in my organisation need a myID?</h3>
                                            <p>
                                                No. Only staff who need to access the NMI Services Portal will need a myID.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-06' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>Should I use a personal email or a work email for myID?</h3>
                                            <p>
                                                You should use a personal email to set up your myID.
                                                myID is your personal digital identity that allows you to prove who you are when you log in to online government services.
                                                You could lose access to your work email. If this happens, you cannot change your email address in the myID app.
                                                You also cannot set up another myID account with a different email address.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-07' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>
                                                What level of identity strength is required for myID and access to the NMI Services Portal?
                                            </h3>
                                            <p>
                                                You can access the NMI Services Portal using Standard identity strength.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-08' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>What is Relationship Authorisation Manager (RAM)?</h3>
                                            <p>
                                                RAM is an Australian Government authorisation service that allows you to act on behalf of your organisation online.
                                                {' It allows you to manage your business authorisations in one place. See the '}
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://info.authorisationmanager.gov.au/'
                                                >
                                                    RAM website
                                                </InTextLink>
                                                {' for more information.'}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-09' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>How do I link my business to RAM?</h3>
                                            <p>
                                                Before you and your staff can access the NMI Services Portal, you need to link your organisation to RAM.
                                                {' See '}
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://info.authorisationmanager.gov.au/get-started'
                                                >
                                                    How to link your business
                                                </InTextLink>
                                                {' for more information. Only Principal Authority\'s need to do this.'}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-10' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>What is a Principal Authority and Authorisation Administrator?</h3>
                                            <p>
                                                {'A Principal Authority is a person responsible for the business/organisation (see '}
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://info.authorisationmanager.gov.au/principal-authority'
                                                >
                                                    Principal Authority
                                                </InTextLink>
                                                {' for more information). '}
                                                The Principal Authority can link the organisation to RAM and create authorisations for other employees to access the NMI Services Portal on behalf of the organisation.
                                            </p>
                                            <p>
                                                The Principal Authority can authorise someone as an Authorisation Administrator.
                                                The Authorisation Administrator can also create and manage authorisations for other staff members.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-11' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>How do I link my myID to RAM?</h3>
                                            <p>
                                                Before staff in your organisation can access the NMI Services Portal, their myID must be linked to RAM. The Principal Authority or Authorisation Administrator within your organisation must create authorisations for staff members to access the NMI Services Portal on behalf of the organisation.
                                            </p>
                                            <p>
                                                {' See '}
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://info.authorisationmanager.gov.au/set-up-authorisations'
                                                >
                                                    Set up authorisations
                                                </InTextLink>
                                                {' for more information.'}
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-12' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>Will the people in your organisation who are responsible for Relationship Authorisation Manager (RAM) be able to see all the (personal/family) businesses that an employee is currently authorised for?</h3>
                                            <p>
                                                The answer is NO. That is because the principal authority for your organisation is only authorised/able to see information relating to it.
                                                As they are not the principal authority for the other organisations that an employee may be authorised to represent, the principal authority for your organisation will have no access to any data/information relating to those.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-13' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>Will an employee that has been authorised to represent your organisation (using myID and RAM) be able to access any details regarding the organisation (Financial or other)?</h3>
                                            <p>
                                                The answer is NO. Authorisation, using RAM, only authorises the employee to transact with the NMI within the NMI Services Portal. They do not gain access to organisation information (Financial or other).
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-14' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>
                                                How will the organisation be able to remove an individual&apos;s access to the portal after they have left the organisation?
                                            </h3>
                                            <p>
                                                It is the responsibility of the organisation to remove an individual as an authorised representative of that organisation.
                                                This disconnection or de-authorisation is done by the principal authority or authorisation administrator within the organisation via the RAM tool.
                                            </p>
                                            <p>
                                                Once an employee has been de-authorised, they will be unable to access the NMI Services Portal.
                                                {' See '}
                                                <InTextLink
                                                    target='_blank'
                                                    href='https://info.authorisationmanager.gov.au/manage-authorisations'
                                                >
                                                    Information on set-up and managing authorisations
                                                </InTextLink>
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <Row className='mb-4'>
                                <Col>
                                    <div className='anchor-section' id='faqs-contactus' tabIndex={-1}>
                                        <div className='body-copy reset-last mb-4' lang='en'>
                                            <h3 className='mb-2'>Where can I go for more help?</h3>
                                            <ul className='list-unstyled'>
                                                <li>
                                                    {'Visit the myID '}
                                                    <InTextLink
                                                        target='_blank'
                                                        href='https://www.myid.gov.au/help#i-no-longer-have-access-to-the-email-address-i-used-to-set-up-my-mygovid-'
                                                    >
                                                        &apos;Need help?&apos; webpage
                                                    </InTextLink>
                                                </li>
                                                <li>
                                                    {'Visit RAM '}
                                                    <InTextLink
                                                        target='_blank'
                                                        href='https://info.authorisationmanager.gov.au/help-managing-business-links#where_to_go_for_additional_help_or_support'
                                                    >
                                                        &apos;Help&apos; webpage
                                                    </InTextLink>
                                                </li>
                                                <li>
                                                    {'Email: '}
                                                    <a className='nmi-in-text-link' href='mailto:infotm@measurement.gov.au'>
                                                        infotm@measurement.gov.au
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {/* <Row>
                        <Col md={4} lg={3} />
                        <Col md={8} lg={9}>
                            <Row className='mb-4'>
                                <Col>
                                    <CustomAccordion id='faqs-01' containerClassName='static-content'>
                                        <CustomAccordionBody name='What NMI services are available to clients via the NMI Services Portal?' eventKey='1' className='mb-4 py-2'>
                                            <p>The NMI Services Portal enables clients to access NMI’s physical Test and Calibration services. </p>
                                        </CustomAccordionBody>
                                    </CustomAccordion>
                                </Col>
                            </Row>
                        </Col>
                    </Row> */}
                    <Row>
                        {/* <Col md={4} lg={3} />
                        <Col md={8} lg={9}> */}
                        <Col>
                            <div className='mt-5 d-grid w-100 gap-3 d-md-flex justify-content-md-between'>
                                <Link
                                    data-testid='back-to-help-guide'
                                    to='/help-guide'
                                    replace
                                    className='btn btn-tertiary'
                                >
                                    <i className='icon-back me-1' aria-hidden='true' />
                                    Back to help guide
                                </Link>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default FAQs;
